import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import DistributionChart from "../components/DistributionChart.tsx";
import CountryName from "../components/CountryName.tsx";
import FormattedDate from "../components/FormattedDate.tsx";
import FilterBar from "../components/filters/FilterBar.tsx";
import SavedViewsMenu from "../components/filters/SavedViewsMenu.tsx";
import type { FilterSpec } from "../components/filters/specs.ts";
import { useFilters } from "../components/filters/useFilters.ts";
import { CheckCell, MatchedCell, TitleLinkCell } from "../components/table/cells.tsx";
import EmptyTableRow from "../components/table/EmptyTableRow.tsx";
import ScrollTable from "../components/table/ScrollTable.tsx";
import SortableTableHead from "../components/table/SortableTableHead.tsx";
import TableCard from "../components/table/TableCard.tsx";
import UrlTablePagination from "../components/table/UrlTablePagination.tsx";
import { useOrganization } from "../contexts/OrganizationContext.tsx";
import { useCountryOptions } from "../hooks/useCountryOptions.ts";
import { useSourceStats } from "../hooks/useSourceStats.ts";
import { useCommaParam, useUrlPaging, useUrlSort, useUrlState } from "../hooks/useUrlState.ts";
import { booleanFilterToParam, parseBooleanFilter } from "../utils/urlFilters.ts";
import { formatNumber } from "../utils/format.ts";
import PageHeader from "../components/PageHeader.tsx";

const COLUMNS = [
  { id: "title", label: "Title" },
  { id: "company_name", label: "Company" },
  { id: null, label: "Country" },
  { id: null, label: "City" },
  { id: null, label: "Source" },
  { id: null, label: "Remote" },
  { id: "published_at", label: "Published" },
  { id: null, label: "Matched" },
];

function JobAdsPage() {
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();
  const url = useUrlState();
  const { searchParams } = url;

  const { data: filterOptions } = useQuery({
    queryKey: keys.jobAd.filterOptions(),
    queryFn: () => api.getJobAdFilterOptions(),
    staleTime: 30 * 60 * 1000,
  });
  const countryOptions = useCountryOptions(filterOptions?.countries);

  const specs = useMemo<FilterSpec[]>(() => {
    const loading = !filterOptions;
    return [
      {
        kind: "options",
        key: "country",
        label: "Country",
        param: "country",
        pinned: true,
        loading,
        options: countryOptions,
        searchPlaceholder: "Search countries",
      },
      {
        kind: "options",
        key: "source",
        label: "Source",
        param: "source",
        pinned: true,
        loading,
        options: (filterOptions?.sources ?? []).map((s) => ({ value: s, label: s })),
      },
      { kind: "text", key: "company", label: "Company", param: "company", placeholder: "Company name" },
      { kind: "boolean", key: "remote", label: "Remote", param: "remote" },
      {
        kind: "boolean",
        key: "matched",
        label: "Matched",
        param: "matched",
        labels: { yes: "Matched", no: "Unmatched" },
      },
      { kind: "dateRange", key: "published", label: "Published", fromParam: "pub_from", toParam: "pub_to" },
    ];
  }, [filterOptions, countryOptions]);

  const filters = useFilters(specs, {
    state: url,
    search: { param: "title", placeholder: "Search job ads" },
    pageKey: "job-ads",
    scopeId: null,
    remember: true,
  });

  const countries = useCommaParam(searchParams, "country");
  const sources = useCommaParam(searchParams, "source");
  const param = (key: string) => searchParams.get(key) || undefined;
  const yesNo = (key: string) => booleanFilterToParam(parseBooleanFilter(searchParams.get(key)));

  const { sortBy, sortDir, handleSort } = useUrlSort(url, "published_at", "desc");
  const paging = useUrlPaging(url);

  const queryParams = {
    title: param("title"),
    company_name: param("company"),
    country: countries.length ? countries : undefined,
    source: sources.length ? sources : undefined,
    remote: yesNo("remote"),
    matched: yesNo("matched"),
    published_from: param("pub_from"),
    published_to: param("pub_to"),
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: paging.rowsPerPage,
    offset: paging.offset,
  };

  const { data, isLoading } = useQuery({
    queryKey: keys.jobAd.list(queryParams),
    queryFn: () => api.getJobAds(queryParams),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const stats = useSourceStats(keys.jobAd.stats(), api.getJobAdStats, (s) => ({
    label: s.source,
    count: s.count,
  }));

  return (
    <>
      <PageHeader
        title="Job ads"
        description="Job postings from public job boards, matched to the companies that posted them"
      />
      <DistributionChart
        title="Job ads per source"
        data={stats.chartData}
        loading={stats.isLoading}
      />
      <TableCard
        loading={isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            count={data ? `${formatNumber(data.total)} ${data.total === 1 ? "job ad" : "job ads"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filters}
                context={{ organizationId: currentOrg?.id, organizationName: currentOrg?.name }}
              />
            }
          />
        }
        footer={data && data.total > 0 && <UrlTablePagination total={data.total} paging={paging} />}
      >
        <ScrollTable>
          <SortableTableHead
            columns={COLUMNS}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
          <TableBody>
            {data?.items.map((ad) => (
              <TableRow
                key={ad.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/job-ads/${ad.id}`)}
              >
                <TitleLinkCell title={ad.title} href={ad.url} />
                <TableCell>{ad.company_name}</TableCell>
                <TableCell>
                  <CountryName code={ad.country} />
                </TableCell>
                <TableCell>{ad.city ?? "-"}</TableCell>
                <TableCell>{ad.source}</TableCell>
                <CheckCell checked={ad.remote} label="Remote" onFalse="no" />
                <TableCell>
                  <FormattedDate value={ad.published_at} />
                </TableCell>
                <MatchedCell matched={!!ad.master_company_id} />
              </TableRow>
            ))}
            {data?.items.length === 0 && (
              <EmptyTableRow colSpan={COLUMNS.length}>
                {filters.activeCount > 0 ? "No job ads match your filters" : "No job ads found"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </>
  );
}

export default JobAdsPage;
